import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import TicketsPopup from './TicketsPopup';
import { useApi } from '../hooks/useApi';
import { CAP } from '../utils/permissions';
import { usePermissions, inertClasses } from '../utils/PermissionContext';

// Safely render Firestore values that may arrive as raw typed objects
// (e.g. {doubleValue: ...}, {integerValue: ...}, {stringValue: ...}).
const renderValue = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') {
        if ('stringValue' in val) return val.stringValue;
        if ('integerValue' in val) return val.integerValue;
        if ('doubleValue' in val) return val.doubleValue;
        if ('booleanValue' in val) return String(val.booleanValue);
        if ('nullValue' in val) return '';
        return JSON.stringify(val);
    }
    return String(val);
};

const formatHours = (val) => {
    if (val === null || val === undefined || val === '') return '0.00';
    let raw = val;
    if (typeof val === 'object') {
        if ('doubleValue' in val) raw = val.doubleValue;
        else if ('integerValue' in val) raw = val.integerValue;
        else if ('stringValue' in val) raw = val.stringValue;
    }
    const num = Number(raw);
    return isNaN(num) ? renderValue(val) : num.toFixed(2);
};

// Pill-style status toggle. Defaults to green (on) / gray (off); callers can
// override the colour scheme (e.g. amber/teal for Postpaid, like the dashboard).
//
// `readOnly` keeps the pill exactly where it is but stops it being a control:
// the status of a domain is information a Read-only admin needs, so hiding
// these would hide whether the account has access, is postpaid, or is active.
// Same treatment as the equivalent pills on the admin customer screens.
const StatusPill = ({
  on,
  onLabel,
  offLabel,
  onClick,
  readOnly = false,
  onClass = 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200 hover:border-green-400',
  offClass = 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200 hover:border-gray-400',
  dotOnClass = 'bg-green-600',
  dotOffClass = 'bg-gray-500',
}) => (
  <button
    type="button"
    onClick={readOnly ? undefined : onClick}
    disabled={readOnly}
    // No "Toggle" tooltip when it does not toggle. The hover/active colour
    // classes are dropped too, so it stops advertising itself as clickable.
    title={readOnly ? undefined : `Toggle (currently ${on ? onLabel : offLabel})`}
    className={`text-xs font-bold uppercase px-3 py-1.5 rounded-full border transition-all duration-200 select-none flex items-center gap-1.5 focus:outline-none ${
      readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-105 active:scale-95'
    } ${inertClasses(on ? onClass : offClass, readOnly)}`}
  >
    <span className={`h-1.5 w-1.5 rounded-full ${on ? dotOnClass : dotOffClass}`}></span>
    {on ? onLabel : offLabel}
  </button>
);

function CustomerSummary({ customer, t, onCustomerSave, googleIdToken }) {
  // Three different questions, which a single `isAdmin` prop used to conflate:
  //
  //   seeing the internal status of the customer  -> selectAnyCustomer
  //   changing it                                 -> adminWrite
  //   following a link into /admin/*               -> viewAdminScreens
  //
  // The pills follow selectAnyCustomer, not viewAdminScreens: they describe the
  // customer being viewed, and selectAnyCustomer is precisely "you are staff
  // looking at someone else's account". That keeps them for Read-only, which
  // has no admin section at all any more but still needs to see whether the
  // domain it is looking at has access, is postpaid and is active. An actual
  // customer holds none of the three, so the row stays absent for them.
  //
  // adminWrite rather than writeCustomerProfile for changing them: hasAccess /
  // isPostpaid / isActive are operator flags, which is exactly why they are
  // excluded from permissions.SELF_EDITABLE_CUSTOMER_FIELDS on the server.
  const { can } = usePermissions();
  const canSeeInternalStatus = can(CAP.SELECT_ANY_CUSTOMER);
  const canChangeInternalStatus = can(CAP.ADMIN_WRITE);
  // Separate from the pills on purpose: Read-only would follow this link to a
  // route it can no longer reach and get bounced back to the dashboard.
  const canOpenAdminScreens = can(CAP.VIEW_ADMIN_SCREENS);
  const [localHasAccess, setLocalHasAccess] = useState(false);
  const [localIsPostpaid, setLocalIsPostpaid] = useState(false);
  const [localIsActive, setLocalIsActive] = useState(true);
  const [localIsAutoInvoiceEnabled, setLocalIsAutoInvoiceEnabled] = useState(true);
  const customerId = customer?.customerId || customer?.id;
  const customerHasAccess = customer?.hasAccess;
  const customerIsPostpaid = customer?.isPostpaid;
  const customerIsActive = customer?.isActive;
  const customerIsAutoInvoiceEnabled = customer?.isAutoInvoiceEnabled;

  const [showTicketsPopup, setShowTicketsPopup] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [orders, setOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const { apiRequest } = useApi(googleIdToken);

  useEffect(() => {
    // Check if customerId exists instead of checking the 'customer' object
    if (customerId) {
      setLocalHasAccess(customerHasAccess);
      setLocalIsPostpaid(customerIsPostpaid || false);
      setLocalIsActive(customerIsActive !== false);
      setLocalIsAutoInvoiceEnabled(customerIsAutoInvoiceEnabled !== false);
    }
  }, [customerId, customerHasAccess, customerIsPostpaid, customerIsActive, customerIsAutoInvoiceEnabled]);

  // Reset tickets and orders when customerId changes so that they are refetched
  useEffect(() => {
    setTickets([]);
    setOrders([]);
  }, [customerId]);

  // Persist a single field immediately (optimistic update applied by caller).
  // Reverts the optimistic state if the save fails.
  const persistCustomer = async (updates, revert) => {
    try {
      await onCustomerSave(customerId, updates);
      toast.success(t.saved || "Saved.");
    } catch (error) {
      console.error("Failed to save account setting:", error);
      if (revert) revert();
      toast.error(t.save_failed || "Failed to save.");
    }
  };

  const handleToggle = () => {
    const newVal = !localHasAccess;
    setLocalHasAccess(newVal);
    persistCustomer({ hasAccess: newVal }, () => setLocalHasAccess(!newVal));
  };

  const handlePostpaidToggle = () => {
    const newVal = !localIsPostpaid;
    setLocalIsPostpaid(newVal);
    persistCustomer({ isPostpaid: newVal }, () => setLocalIsPostpaid(!newVal));
  };

  const handleActiveToggle = () => {
    const newVal = !localIsActive;
    setLocalIsActive(newVal);
    persistCustomer({ isActive: newVal }, () => setLocalIsActive(!newVal));
  };

  const handleAutoInvoiceToggle = () => {
    const newVal = !localIsAutoInvoiceEnabled;
    setLocalIsAutoInvoiceEnabled(newVal);
    persistCustomer({ isAutoInvoiceEnabled: newVal }, () => setLocalIsAutoInvoiceEnabled(!newVal));
  };

  const handleShowTickets = async () => {
    setShowTicketsPopup(true);
    if (tickets.length === 0) {
      setIsLoadingTickets(true);
      try {
        const data = await apiRequest(`/api/customers/${customerId}/tickets`);
        if (data && !data.error) {
          setTickets(data);
        } else {
          console.error("Failed to fetch tickets", data);
        }
      } catch (error) {
        console.error("Error fetching tickets:", error);
      } finally {
        setIsLoadingTickets(false);
      }
    }
    
    if (orders.length === 0) {
      setIsLoadingOrders(true);
      try {
        const data = await apiRequest(`/api/customers/${customerId}/orders?limit=100`);
        if (data && data.orders) {
          const supportOrders = data.orders.filter(order => 
             order.status !== 'Failed' && order.cartItems && order.cartItems.some(item => item.hours != null)
          );
          setOrders(supportOrders);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setIsLoadingOrders(false);
      }
    }
  };

  if (!customer) {
    return null;
  }

  return (
    <div className="bg-white p-6 rounded-lg mb-8 tour-customer-summary">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="text-3xl font-bold text-gray-800 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 text-gray-800 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
          {t.my_overview}
          {canOpenAdminScreens && customerId && (
            <Link to={`/admin/customers/${customerId}`} className="ml-3 text-blue-500 hover:text-blue-700 transition-colors" title="Customer Details">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </Link>
          )}
        </h2>

        {canSeeInternalStatus && (
          <div className="flex items-center gap-3 flex-wrap">
            <StatusPill
              on={localHasAccess}
              onLabel={t.has_access || 'Has Access'}
              offLabel={t.no_access || 'No Access'}
              onClick={handleToggle}
              readOnly={!canChangeInternalStatus}
            />
            <StatusPill
              on={localIsPostpaid}
              onLabel={t.postpaid_customer || 'Postpaid'}
              offLabel={t.prepaid_customer || 'Prepaid'}
              onClick={handlePostpaidToggle}
              readOnly={!canChangeInternalStatus}
              onClass="bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200 hover:border-amber-400"
              offClass="bg-teal-100 text-teal-800 border-teal-300 hover:bg-teal-200 hover:border-teal-400"
              dotOnClass="bg-amber-600"
              dotOffClass="bg-teal-600"
            />
            <StatusPill
              on={localIsActive}
              onLabel={t.active || 'Active'}
              offLabel={t.inactive || 'Inactive'}
              onClick={handleActiveToggle}
              readOnly={!canChangeInternalStatus}
            />
            <StatusPill
              on={localIsAutoInvoiceEnabled}
              onLabel={t.auto_invoice_on || 'Auto Invoice ON'}
              offLabel={t.auto_invoice_off || 'Auto Invoice OFF'}
              onClick={handleAutoInvoiceToggle}
              readOnly={!canChangeInternalStatus}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-600 text-lg mb-2">{t.primary_domain}</h3>
          <p className="text-gray-800 text-xl">{renderValue(customer.customerDomain)}</p>
        </div>
        <div
          className="bg-gray-50 p-4 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={handleShowTickets}
        >
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-600 text-lg mb-2">
              {t.support_hours} in <span className="text-primary">{new Date().getFullYear()}</span>
            </h3>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-800 text-xl">
            <span className="font-bold text-green-600">{formatHours(customer.remainingHours)}</span>
            <span className="text-gray-500"> / {customer.purchasedHoursByYear?.[new Date().getFullYear()] || 0} {t.available ? t.available.toLowerCase() : ''}</span>
          </p>
        </div>
      </div>

      <TicketsPopup
        isOpen={showTicketsPopup}
        onClose={() => setShowTicketsPopup(false)}
        tickets={tickets}
        isLoading={isLoadingTickets}
        orders={orders}
        isLoadingOrders={isLoadingOrders}
        t={t}
        // Drives the links to /admin/afas-transactions/<id> in the timeline, so
        // it is the "may see admin screens" flag — not the pills' flag, and not
        // the write one.
        isAdmin={canOpenAdminScreens}
      />
    </div>
  );
}

export default CustomerSummary;
